import { useParams, Navigate, Link } from "react-router-dom";
import {
  MapPin,
  Phone,
  MessageCircle,
  ShieldCheck,
  Award,
  LifeBuoy,
  Clock,
  Check,
  ArrowRight,
} from "lucide-react";
import SeoHead from "@/components/SeoHead";
import PageHeader from "@/components/layout/PageHeader";
import BrandShowcaseSection from "@/components/sections/BrandShowcaseSection";
import { AREA_LANDINGS } from "@/data/areaLandings";
import { BRAND, buildWhatsAppLink } from "@/data/site";

export default function AreaAkuDegisimi() {
  const { slug } = useParams();
  const data = AREA_LANDINGS[slug];
  if (!data) return <Navigate to="/" replace />;

  const waText = `Merhaba, Eskişehir ${data.area} bölgesindeyim. Yerinde akü değişimi için konumumu paylaşıyorum.`;
  const canonical = `https://eskisehirakudegisim.com.tr/${data.slug}`;

  return (
    <div data-testid={`area-page-${data.slug}`}>
      <SeoHead
        title={data.title}
        description={data.description}
        canonical={canonical}
      />

      <PageHeader
        eyebrow={`Eskişehir · ${data.area}`}
        title={`${data.area}`}
        accent="Akü Değişimi"
        subtitle={data.heroLine}
      />

      {/* Intro + quick CTAs */}
      <section className="relative py-16">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 grid lg:grid-cols-3 gap-10">
          <div className="lg:col-span-2">
            <p className="text-white/75 text-base sm:text-lg leading-relaxed">
              {data.intro}
            </p>

            <ul className="mt-8 grid sm:grid-cols-2 gap-3">
              {[
                {
                  icon: Clock,
                  t: `${data.area}'nda Aynı Gün Servis`,
                },
                { icon: LifeBuoy, t: "6 Ay Ücretsiz Yol Yardımı" },
                { icon: ShieldCheck, t: "Tüm Akülerde 2 Yıl Garanti" },
                {
                  icon: Award,
                  t: "Start-Stop (BSG/AGM) Akülerde 3 Yıl Garanti",
                },
              ].map(({ icon: Icon, t }) => (
                <li
                  key={t}
                  className="flex items-center gap-3 p-3 rounded-xl glass"
                >
                  <Icon className="w-4 h-4 text-gold flex-shrink-0" />
                  <span className="text-sm text-white/90">{t}</span>
                </li>
              ))}
            </ul>

            <div className="mt-8 flex flex-col sm:flex-row gap-3">
              <a
                href={`tel:${BRAND.phoneTel}`}
                data-testid="area-call-cta"
                className="gradient-gold text-black font-semibold px-7 py-3.5 inline-flex items-center gap-2 justify-center hover:shadow-[0_0_30px_rgba(212,175,55,0.4)] transition-all"
              >
                <Phone className="w-4 h-4" /> {BRAND.phone}
              </a>
              <a
                href={buildWhatsAppLink(waText)}
                target="_blank"
                rel="noreferrer"
                data-testid="area-whatsapp-cta"
                className="bg-[#25D366] hover:bg-[#1ebd5a] text-white font-semibold px-7 py-3.5 inline-flex items-center gap-2 justify-center transition-all"
              >
                <MessageCircle className="w-4 h-4" /> WhatsApp ile Konum Gönder
              </a>
            </div>
          </div>

          {/* Side card */}
          <aside className="glass-strong rounded-2xl p-6 h-fit">
            <div className="flex items-center gap-3 mb-5">
              <div className="w-10 h-10 rounded-lg bg-gold/15 border border-gold/40 flex items-center justify-center">
                <MapPin className="w-4 h-4 text-gold" />
              </div>
              <div>
                <div className="text-[10px] uppercase tracking-[0.25em] text-gold font-bold">
                  Hizmet Bölgesi
                </div>
                <div className="font-display font-bold text-white">
                  {data.area}, Eskişehir
                </div>
              </div>
            </div>
            <p className="text-white/65 text-sm leading-relaxed mb-4">
              Aşağıdaki mahalle ve çevrelere yerinde akü değişimi yapıyoruz:
            </p>
            <ul className="space-y-2">
              {data.neighbourhoods.map((n) => (
                <li
                  key={n}
                  className="flex items-center gap-2 text-sm text-white/85"
                >
                  <Check className="w-3.5 h-3.5 text-gold flex-shrink-0" />
                  {n}
                </li>
              ))}
            </ul>
          </aside>
        </div>
      </section>

      <BrandShowcaseSection />

      {/* Bottom CTA + internal link to acil page */}
      <section className="py-16 bg-[#050A1F] relative overflow-hidden">
        <div className="absolute inset-0 bg-blueprint opacity-20" />
        <div className="relative max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 grid lg:grid-cols-[1fr_auto] gap-6 items-center">
          <div>
            <h2 className="font-display text-2xl sm:text-3xl font-bold text-white text-balance leading-tight">
              {data.area}'nda akünüz mü bitti? Hemen ulaşın.
            </h2>
            <p className="text-white/70 text-sm mt-2 max-w-2xl">
              Eskişehir genelinde acil akü değişimi süreçleri için detaylı bilgi
              alın.
            </p>
          </div>
          <div className="flex flex-col sm:flex-row gap-3">
            <Link
              to="/eskisehir-acil-aku-degisimi"
              className="border border-gold text-gold hover:bg-gold/10 font-semibold px-6 py-3.5 inline-flex items-center gap-2 justify-center transition-all"
            >
              Acil Akü Değişimi <ArrowRight className="w-4 h-4" />
            </Link>
            <a
              href={buildWhatsAppLink(waText)}
              target="_blank"
              rel="noreferrer"
              className="bg-[#25D366] hover:bg-[#1ebd5a] text-white font-semibold px-6 py-3.5 inline-flex items-center gap-2 justify-center transition-all"
            >
              <MessageCircle className="w-4 h-4" /> WhatsApp
            </a>
          </div>
        </div>
      </section>
    </div>
  );
}
